import { useCallback, useEffect, useMemo } from 'react';
import classnames from 'classnames/bind';
import { FormField, Indication, RangeSlider } from 'taboola-ultimate-ui';
import { useAvailableItems } from 'hooks';
import { SupplyTargetingIcons } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/components/SupplyTargetingIcons/SupplyTargetingIcons';
import {
    SUPPLY_TARGETING_TYPES,
    supplyTargetingSliderOptions,
} from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/const';
import { useAPISupplyTargetingRestrictions } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/hooks/useAPISupplyTargetingRestrictions';
import { useSupplyTargetingFormField } from 'modules/campaigns/modules/common-campaign-form/components/SupplyTargeting/hooks/useSupplyTargetingFormField';
import { getDefaultSupplyTargeting } from 'modules/campaigns/modules/common-campaign-form/config/InitialValues';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import useFormFieldValue from 'modules/taboola-common-frontend-modules/formData/hooks/useFormFieldValue';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import styles from './SupplyTargetingSlider.module.scss';

const classNameBuilder = classnames.bind(styles);

const MIN_SCROLL = 0;
const STEP_SCROLL = 1;

export const SupplyTargetingSlider = () => {
    const { formatMessage } = useIntl();
    const { value: campaignSupplyTargeting, onChange: campaignSupplyTargetingOnChange } = useSupplyTargetingFormField();
    const { value: creativeType } = useFormFieldValue({ field: 'eligibilityType' });
    const { supplyTargetingResource, isSupplyPermitted, isTaboolaDefaultValue } = useAPISupplyTargetingRestrictions();
    const useYahooMomentsSlider = useConfigMatch({
        [FEATURE_FLAGS.SUPPLY_TARGETING_USE_YAHOO_MOMENTS_SLIDER_ENABLED]: 'true',
    });
    const availableOptions = useAvailableItems(supplyTargetingSliderOptions, {
        supplyTargetingResource,
        campaignSupplyTargeting,
        creativeType,
        useYahooMomentsSlider,
    });
    const MAX_SCROLL = availableOptions.length - 1;
    const availableSupplyTargetingOptions = useMemo(() => {
        return availableOptions.reduce((acc, item, index) => {
            acc[index] = item;
            return acc;
        }, []);
    }, [availableOptions]);

    const isSelectedSupplyTargetingAvailable = useMemo(() => {
        const isSelectionYahooMoments = campaignSupplyTargeting === SUPPLY_TARGETING_TYPES.YAHOO_MOMENTS;
        if (isSelectionYahooMoments) {
            return true;
        }
        return availableOptions.some(option => option.value === campaignSupplyTargeting);
    }, [availableOptions, campaignSupplyTargeting]);

    useEffect(() => {
        const defaultSupplyTargeting = getDefaultSupplyTargeting({
            isSupplyPermitted,
            isTaboolaDefaultValue,
            isPermitted: true,
        });
        if (isSelectedSupplyTargetingAvailable) {
            return;
        }
        campaignSupplyTargetingOnChange(defaultSupplyTargeting);
    }, [campaignSupplyTargetingOnChange, isSelectedSupplyTargetingAvailable, isSupplyPermitted, isTaboolaDefaultValue]);

    const selectedValueId = useMemo(
        () =>
            (availableSupplyTargetingOptions || []).findIndex(
                supplyTargeting => supplyTargeting.value === campaignSupplyTargeting
            ),
        [campaignSupplyTargeting, availableSupplyTargetingOptions]
    );

    const changeHandler = useCallback(
        (event, newValue) => {
            const newSupplyTargeting = availableSupplyTargetingOptions[newValue].value;
            campaignSupplyTargetingOnChange(newSupplyTargeting);
        },
        [campaignSupplyTargetingOnChange, availableSupplyTargetingOptions]
    );

    if (!isSupplyPermitted) {
        return null;
    }

    return (
        <FormField
            inputId="supply-targeting"
            label={
                <FormattedMessage
                    id="campaign.editor.targeting.supplyTargeting.title"
                    defaultMessage="Supply Targeting"
                />
            }
            containerClass={styles['input']}
        >
            <div className={styles['slider-container']}>
                <div className={styles['slider-wrapper']}>
                    <div className={classNameBuilder('slider-label-start')}>
                        <span>
                            <FormattedMessage
                                id={`campaign.editor.targeting.supply.slider.label.${
                                    availableSupplyTargetingOptions[MIN_SCROLL]?.id ?? ''
                                }`}
                            />
                        </span>
                    </div>
                    <RangeSlider
                        value={selectedValueId}
                        min={MIN_SCROLL}
                        max={MAX_SCROLL}
                        step={STEP_SCROLL}
                        marks
                        onChange={changeHandler}
                        aria-labelledby="supply-targeting-slider"
                        valueLabelFormat={() =>
                            formatMessage({
                                id: `campaign.editor.targeting.supply.slider.${campaignSupplyTargeting}`,
                            })
                        }
                        style={{ fontSize: '10px' }}
                    />
                    <div className={classNameBuilder('slider-label-end')}>
                        <FormattedMessage
                            id={`campaign.editor.targeting.supply.slider.label.${
                                availableSupplyTargetingOptions[MAX_SCROLL]?.id ?? ''
                            }`}
                        />
                    </div>
                </div>

                {availableSupplyTargetingOptions[selectedValueId] && (
                    <div className={styles['indication-wrapper']}>
                        <Indication
                            type="info"
                            displayDismissComponent={false}
                            message={
                                <div>
                                    <div>
                                        <FormattedMessage
                                            id={`campaign.editor.targeting.supply.slider.title.${campaignSupplyTargeting}`}
                                        />
                                    </div>
                                    <FormattedMessage
                                        id={`campaign.editor.targeting.supply.slider.subtext.${campaignSupplyTargeting}`}
                                    />
                                    <div className={styles['divider']} />
                                    <SupplyTargetingIcons
                                        supplyTargeting={availableSupplyTargetingOptions[selectedValueId]}
                                    />
                                </div>
                            }
                        />
                    </div>
                )}
            </div>
        </FormField>
    );
};
